import { headerService, HeaderType } from './headers';
import { tokenRefreshService } from './tokenRefresh';

export interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
  error?: string;
}

export interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export class HttpClient {
  private baseUrl: string;
  private timeout: number;

  constructor(baseUrl: string, timeout: number = 10000) {
    this.baseUrl = baseUrl;
    this.timeout = timeout;
  }

  private async request<T>(
    endpoint: string,
    options: any = {},
    headerType: any = 'public'
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    const baseHeaders = headerService.getHeaders(headerType);
    const cleanHeaders: Record<string, string> = {};
    Object.entries(baseHeaders).forEach(([key, value]) => {
      if (value !== undefined) {
        cleanHeaders[key] = value;
      }
    });

    const config: any = {
      headers: {
        ...cleanHeaders,
        ...options.headers,
      },
      ...options,
    };

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), this.timeout);

      const response = await fetch(url, {
        ...config,
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      const contentType = response.headers.get('content-type') || '';

      if (!response.ok) {
        // Intentar refresh en 401
        if (response.status === 401 && headerType === 'authenticated') {
          try {
            const refreshSuccess = await tokenRefreshService.refreshTokenIfNeeded();
            if (refreshSuccess) {
              return this.request<T>(endpoint, options, headerType);
            } else {
              headerService.clearAuthToken();
            }
          } catch (refreshError) {
            console.error('❌ Error en refresh inteligente:', refreshError);
            headerService.clearAuthToken();
          }
        }

        let errorBody: any = null;
        let rawText: string | null = null;
        try {
          if (contentType.includes('application/json')) {
            errorBody = await response.json();
          } else {
            rawText = await response.text();
          }
        } catch {
          // ignore parse errors
        }

        // Construir mensaje amigable; para 4xx priorizar body.message si existe
        let message = `HTTP ${response.status}`;
        const code = (errorBody && (errorBody.code || errorBody.errorCode)) || undefined;
        if (response.status >= 400 && response.status < 500) {
          if (errorBody?.message) {
            message = errorBody.message;
          } else if (rawText) {
            message = rawText;
          }
        } else {
          // 5xx
          message = errorBody?.message || 'Ocurrió un error en el servidor';
        }

        throw new ApiError(message, response.status, code);
      }

      if (contentType.includes('application/json')) {
        const data = await response.json();
        return data;
      }

      // Si no es JSON devolver texto tipado como any
      const text = await response.text();
      return text as unknown as T;
    } catch (error) {
      console.error('🌐 API request failed:', error);
      if (error instanceof Error) {
        if (error.name === 'AbortError') {
          throw new ApiError('Request timeout', 408);
        }
      }
      throw error;
    }
  }

  async get<T>(endpoint: string, params?: Record<string, string | undefined>, headerType: HeaderType = 'public'): Promise<T> {
    let url = endpoint;
    if (params) {
      const cleanParams: Record<string, string> = {};
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          cleanParams[key] = value;
        }
      });
      const searchParams = new URLSearchParams(cleanParams);
      url = `${endpoint}?${searchParams.toString()}`;
    }
    return this.request<T>(url, { method: 'GET' }, headerType);
  }

  async post<T>(endpoint: string, body: any, headerType: HeaderType = 'public'): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: JSON.stringify(body),
    }, headerType);
  }

  async put<T>(endpoint: string, body: any, headerType: HeaderType = 'public'): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: JSON.stringify(body),
    }, headerType);
  }

  async patch<T>(endpoint: string, body: any, headerType: HeaderType = 'public'): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }, headerType);
  }

  async delete<T>(endpoint: string, headerType: HeaderType = 'public'): Promise<T> {
    return this.request<T>(endpoint, { method: 'DELETE' }, headerType);
  }
}

export const httpClient = new HttpClient(
  import.meta.env.VITE_API_BASE_URL,
  import.meta.env.VITE_API_TIMEOUT
);

export class ApiError extends Error {
  public status: number;
  public code?: string;

  constructor(message: string, status: number, code?: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }
}

export const handleApiError = (error: any): never => {
  if (error instanceof ApiError) {
    throw error;
  }

  if (error instanceof Error) {
    // Intentar extraer status de mensajes anteriores
    const statusMatch = error.message.match(/status: (\d+)/);
    const status = statusMatch ? parseInt(statusMatch[1]) : 500;
    throw new ApiError(error.message, status);
  }

  throw new ApiError('Unknown error occurred', 500);
}; 